import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { investmentsService, usersService } from "../services/api";
import {
  formatCurrency,
  toPersianNumbers,
  toEnglishNumbers,
} from "../utils/numbers";
import {
  Plus,
  Check,
  AlertCircle,
  BarChart3,
  Search,
  Users,
  X,
  Shield,
  Coins,
} from "lucide-react";
import { Modal } from "../components/Modal";
import JalaliDatePicker from "../components/JalaliDatePicker";
import { SearchableSelect } from "../components/SearchableSelect";

interface Investment {
  week?: number;
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  amount: string | number;
  investment_date: string;
  investment_type?: string;
  description?: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  national_code?: string;
  mobile?: string;
  is_active?: number | boolean;
}

export default function Investments() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [investments, setInvestments] = useState<Investment[]>([]);
  const [investors, setInvestors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  // Weekly selection state
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isSubmittingWeekly, setIsSubmittingWeekly] = useState(false);

  const [isIndividualModalOpen, setIsIndividualModalOpen] = useState(false);
  const [individualFormData, setIndividualFormData] = useState({
    user_id: "",
    intrinsic_amount: "",
    value_added_amount: "",
    investment_date: "",
  });

  const [formData, setFormData] = useState({
    week: "",
    amount: "",
    investment_date: "",
  });

  const [summaryData, setSummaryData] = useState({
    user_id: "",
    total: 0,
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenAddModal = () => {
    const maxWeek =
      investments.length > 0
        ? Math.max(...investments.map((i) => i.week || 0))
        : 0;
    setFormData({
      week: String(maxWeek + 1),
      amount: "",
      investment_date: "",
    });
    // Select ALL investors and admins by default!
    setSelectedUserIds(investors.map((u) => u.id));
    setUserSearchQuery("");
    setIsAddModalOpen(true);
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSelectAllUsers = () => {
    setSelectedUserIds(investors.map((u) => u.id));
  };

  const handleDeselectAllUsers = () => {
    setSelectedUserIds([]);
  };

  const filteredInvestors = useMemo(() => {
    if (!userSearchQuery.trim()) return investors;
    const q = userSearchQuery.trim().toLowerCase();
    return investors.filter((u) => {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
      const national = (u.national_code || "").toLowerCase();
      const mobile = (u.mobile || "").toLowerCase();
      return name.includes(q) || national.includes(q) || mobile.includes(q);
    });
  }, [investors, userSearchQuery]);

  const fetchData = async () => {
    try {
      const [investmentsData, usersData] = await Promise.all([
        investmentsService.getInvestments(),
        usersService.getUsers(),
      ]);
      setInvestments(investmentsData);
      const activeInvestors = usersData.filter(
        (u: User) =>
          (u.is_active === 1 ||
            u.is_active === undefined ||
            u.is_active === true) &&
          (u.role === "investor" || u.role === "admin"),
      );
      setInvestors(activeInvestors);
      setSelectedUserIds(activeInvestors.map((u: User) => u.id));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const intrinsic = individualFormData.intrinsic_amount
        ? Number(
            toEnglishNumbers(individualFormData.intrinsic_amount).replace(
              /,/g,
              "",
            ),
          )
        : 0;
      const valueAdded = individualFormData.value_added_amount
        ? Number(
            toEnglishNumbers(individualFormData.value_added_amount).replace(
              /,/g,
              "",
            ),
          )
        : 0;

      if (
        !individualFormData.user_id ||
        !individualFormData.investment_date ||
        (intrinsic === 0 && valueAdded === 0)
      ) {
        alert("لطفا اطلاعات را کامل وارد کنید");
        return;
      }

      await investmentsService.createIndividualInvestment({
        user_id: Number(individualFormData.user_id),
        intrinsic_amount: intrinsic,
        value_added_amount: valueAdded,
        investment_date: individualFormData.investment_date,
      });
      setIsIndividualModalOpen(false);
      setIndividualFormData({
        user_id: "",
        intrinsic_amount: "",
        value_added_amount: "",
        investment_date: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating individual investment:", error);
      alert("خطا در ثبت سرمایه‌گذاری");
    }
  };

  const handleAddInvestment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.week || !formData.amount || !formData.investment_date) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "لطفا تمام فیلدها (هفته، مبلغ و تاریخ) را پر کنید.",
        type: "error",
      });
      return;
    }

    if (selectedUserIds.length === 0) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message:
          "لطفاً حداقل یک نفر از سرمایه‌گذاران یا مدیران را انتخاب کنید.",
        type: "error",
      });
      return;
    }

    setIsSubmittingWeekly(true);
    try {
      await investmentsService.createInvestment({
        week: Number(formData.week),
        amount: Number(formData.amount),
        investment_date: formData.investment_date,
        user_ids: selectedUserIds,
      });

      setFormData({ week: "", amount: "", investment_date: "" });
      setIsAddModalOpen(false);
      fetchData();

      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message: `سرمایه‌گذاری هفتگی با موفقیت برای ${toPersianNumbers(selectedUserIds.length)} نفر ثبت شد.`,
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در ثبت سرمایه‌گذاری.",
        type: "error",
      });
    } finally {
      setIsSubmittingWeekly(false);
    }
  };

  const handleFetchSummary = async (userId: string) => {
    setSummaryData({ ...summaryData, user_id: userId });
    if (!userId) {
      setSummaryData({ user_id: "", total: 0 });
      return;
    }
    try {
      const data = await investmentsService.getInvestmentSummary(
        Number(userId),
      );
      setSummaryData({ user_id: userId, total: data.total_amount });
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const totalInvestments = investments.reduce(
    (sum, inv) => sum + Number(inv.amount),
    0,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-persian-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
            سرمایه‌گذاری‌ها
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            مدیریت و مشاهده سرمایه‌گذاری‌های انجام شده
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {!isAdmin && (
            <span className="flex items-center px-3.5 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
              حالت فقط مشاهده (سرمایه‌گذار)
            </span>
          )}

          <button
            onClick={() => {
              setSummaryData({ user_id: "", total: 0 });
              setIsSummaryModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors shadow-sm"
          >
            <BarChart3 size={20} />
            <span className="font-medium">گزارش سرمایه‌گذار</span>
          </button>

          {isAdmin && (
            <button
              onClick={() => setIsIndividualModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
            >
              <Plus size={20} />
              <span className="font-medium">سرمایه‌گذاری جدید</span>
            </button>
          )}

          <button
            onClick={() => navigate("/weekly-penalties")}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300/40 dark:border-amber-700/40 rounded-xl transition-colors shadow-sm"
          >
            <Coins size={18} />
            <span className="font-medium">جریمه‌های هفتگی</span>
          </button>

          {isAdmin && (
            <button
              onClick={handleOpenAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-persian-600 text-white rounded-xl hover:bg-persian-700 transition-colors shadow-lg shadow-persian-600/20"
            >
              <Plus size={20} />
              <span className="font-medium">ثبت هفتگی</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col justify-center items-center text-center">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">
            مجموع سرمایه‌گذاری‌ها
          </p>
          <p className="text-2xl font-bold text-persian-600 dark:text-persian-400">
            {formatCurrency(totalInvestments)}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  ردیف
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  هفته
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  سرمایه‌گذار
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  نوع سرمایه‌گذاری
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  مبلغ (ریال)
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  تاریخ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
              {investments.length > 0 ? (
                investments.map((inv) => (
                  <tr
                    key={inv.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {toPersianNumbers(inv.id)}
                    </td>
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {toPersianNumbers(inv.week || 1)}
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {inv.first_name} {inv.last_name}
                      </span>
                      {inv.description && (
                        <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {inv.description}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {inv.investment_type === "value_added" ? (
                        <span className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg text-xs font-medium">
                          ارزش افزوده
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-xs font-medium">
                          ارزش ذاتی
                        </span>
                      )}
                    </td>
                    <td className="p-4 font-bold text-persian-600 dark:text-persian-400">
                      {formatCurrency(inv.amount)}
                    </td>
                    <td className="p-4" dir="ltr">
                      {toPersianNumbers(inv.investment_date)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    هیچ سرمایه‌گذاری ثبت نشده است.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Weekly Investment Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-persian-50 dark:bg-persian-900/30 text-persian-600 dark:text-persian-400">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    ثبت سرمایه‌گذاری هفتگی
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    انتخاب افراد، مبلغ و ثبت واریز هفتگی برای سرمایه‌گذاران و
                    مدیران
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleAddInvestment}
              className="p-6 overflow-y-auto space-y-5 flex-1"
            >
              {/* Parameters Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    شماره هفته
                  </label>
                  <input
                    type="number"
                    dir="ltr"
                    value={formData.week}
                    onChange={(e) =>
                      setFormData({ ...formData, week: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
                    placeholder="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    مبلغ هر نفر (ریال)
                  </label>
                  <input
                    type="text"
                    dir="ltr"
                    value={
                      formData.amount
                        ? toPersianNumbers(
                            Number(formData.amount).toLocaleString("en-US"),
                          )
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      const enNumber = toEnglishNumbers(
                        val.replace(/,/g, "").replace(/،/g, ""),
                      );
                      if (/^\d*$/.test(enNumber)) {
                        setFormData({ ...formData, amount: enNumber });
                      }
                    }}
                    className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
                    placeholder="۵,۰۰۰,۰۰۰"
                    required
                  />
                </div>
                <div>
                  <JalaliDatePicker
                    label="تاریخ واریز"
                    value={formData.investment_date}
                    onChange={(date) =>
                      setFormData({ ...formData, investment_date: date })
                    }
                    placeholder="انتخاب تاریخ"
                  />
                </div>
              </div>

              {/* User Selection Section */}
              <div className="space-y-2.5 pt-1 border-t border-slate-100 dark:border-slate-700/60">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Users className="w-4 h-4 text-persian-500" />
                    <span>لیست افراد سرمایه‌گذار و مدیران:</span>
                    <span className="px-2 py-0.5 rounded-full bg-persian-50 dark:bg-persian-950/40 text-persian-600 dark:text-persian-400 font-bold text-xs border border-persian-200 dark:border-persian-800">
                      {toPersianNumbers(selectedUserIds.length)} از{" "}
                      {toPersianNumbers(investors.length)} نفر انتخاب شده
                    </span>
                  </label>
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={handleSelectAllUsers}
                      className="font-bold text-persian-600 hover:text-persian-700 dark:text-persian-400 transition-colors"
                    >
                      انتخاب همه
                    </button>
                    <span className="text-slate-300 dark:text-slate-600">
                      |
                    </span>
                    <button
                      type="button"
                      onClick={handleDeselectAllUsers}
                      className="font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 transition-colors"
                    >
                      لغو انتخاب همه
                    </button>
                  </div>
                </div>

                {/* Search in user list */}
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="جستجوی نام متقاضی، کد ملی یا شماره همراه..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pr-9 pl-4 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none text-slate-800 dark:text-slate-100"
                  />
                  {userSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setUserSearchQuery("")}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Users List Box */}
                <div className="max-h-56 overflow-y-auto space-y-1.5 p-1.5 border border-slate-200 dark:border-slate-700 rounded-2xl bg-slate-50/60 dark:bg-slate-900/40">
                  {filteredInvestors.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-400">
                      هیچ کاربری با این مشخصات یافت نشد
                    </div>
                  ) : (
                    filteredInvestors.map((u) => {
                      const isSelected = selectedUserIds.includes(u.id);
                      return (
                        <div
                          key={u.id}
                          onClick={() => toggleUserSelection(u.id)}
                          className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer transition-all ${
                            isSelected
                              ? "bg-white dark:bg-slate-800 border-persian-400 dark:border-persian-600 shadow-xs ring-1 ring-persian-500/20"
                              : "bg-white/60 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-60 hover:opacity-90"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-colors shrink-0 ${
                                isSelected
                                  ? "bg-persian-600 border-persian-600 text-white shadow-xs"
                                  : "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3.5 h-3.5 stroke-[3]" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate">
                                  {u.first_name} {u.last_name}
                                </span>
                                {u.role === "admin" ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                                    <Shield className="w-2.5 h-2.5" /> مدیر
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                    <Coins className="w-2.5 h-2.5" />{" "}
                                    سرمایه‌گذار
                                  </span>
                                )}
                              </div>
                              {(u.national_code || u.mobile) && (
                                <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                                  {u.national_code && (
                                    <span>
                                      کد ملی:{" "}
                                      {toPersianNumbers(u.national_code)}
                                    </span>
                                  )}
                                  {u.national_code && u.mobile && (
                                    <span>•</span>
                                  )}
                                  {u.mobile && (
                                    <span dir="ltr">
                                      {toPersianNumbers(u.mobile)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <span
                              className={`text-[11px] font-bold ${
                                isSelected
                                  ? "text-persian-600 dark:text-persian-400"
                                  : "text-slate-400"
                              }`}
                            >
                              {isSelected ? "انتخاب شده" : "حذف شده"}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Total Summary Banner */}
              {formData.amount && selectedUserIds.length > 0 && (
                <div className="p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-between text-xs sm:text-sm">
                  <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                    <span className="font-bold">
                      مجموع کل واریزی این هفته (
                      {toPersianNumbers(selectedUserIds.length)} نفر):
                    </span>
                  </div>
                  <span className="font-black text-emerald-700 dark:text-emerald-400 text-base">
                    {formatCurrency(
                      (Number(formData.amount) || 0) * selectedUserIds.length,
                    )}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmittingWeekly || selectedUserIds.length === 0}
                  className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-11 rounded-xl text-sm font-bold transition-all shadow-lg shadow-persian-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmittingWeekly && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  )}
                  <span>
                    {isSubmittingWeekly
                      ? "در حال ثبت سرمایه‌گذاری..."
                      : `ثبت سرمایه‌گذاری برای ${toPersianNumbers(selectedUserIds.length)} نفر`}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={isSubmittingWeekly}
                  className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 h-11 rounded-xl text-sm font-bold transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        isOpen={isIndividualModalOpen}
        onClose={() => setIsIndividualModalOpen(false)}
        title="ثبت سرمایه‌گذاری جدید"
        type="custom"
      >
        <form onSubmit={handleIndividualSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              سرمایه‌گذار
            </label>
            <SearchableSelect
              options={investors.map((user) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name}`,
              }))}
              value={individualFormData.user_id}
              onChange={(val) =>
                setIndividualFormData({ ...individualFormData, user_id: val })
              }
              placeholder="انتخاب کنید"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              ارزش ذاتی (ریال)
            </label>
            <input
              type="text"
              dir="ltr"
              value={individualFormData.intrinsic_amount}
              onChange={(e) => {
                const val = e.target.value
                  .replace(/,/g, "")
                  .replace(/[^0-9۰-۹]/g, "");
                const enVal = toEnglishNumbers(val);
                if (enVal) {
                  setIndividualFormData({
                    ...individualFormData,
                    intrinsic_amount: toPersianNumbers(
                      Number(enVal).toLocaleString(),
                    ),
                  });
                } else {
                  setIndividualFormData({
                    ...individualFormData,
                    intrinsic_amount: "",
                  });
                }
              }}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
              placeholder="۰"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              ارزش افزوده سرمایه (ریال)
            </label>
            <input
              type="text"
              dir="ltr"
              value={individualFormData.value_added_amount}
              onChange={(e) => {
                const val = e.target.value
                  .replace(/,/g, "")
                  .replace(/[^0-9۰-۹]/g, "");
                const enVal = toEnglishNumbers(val);
                if (enVal) {
                  setIndividualFormData({
                    ...individualFormData,
                    value_added_amount: toPersianNumbers(
                      Number(enVal).toLocaleString(),
                    ),
                  });
                } else {
                  setIndividualFormData({
                    ...individualFormData,
                    value_added_amount: "",
                  });
                }
              }}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
              placeholder="۰"
            />
          </div>
          <div>
            <JalaliDatePicker
              label="تاریخ"
              value={individualFormData.investment_date}
              onChange={(date) =>
                setIndividualFormData({
                  ...individualFormData,
                  investment_date: date,
                })
              }
              placeholder="انتخاب تاریخ"
            />
          </div>
          <div className="pt-4 flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold transition-colors shadow-lg shadow-blue-600/20"
            >
              ثبت سرمایه‌گذاری
            </button>
            <button
              type="button"
              onClick={() => setIsIndividualModalOpen(false)}
              className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 py-2.5 rounded-xl text-sm font-bold transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        title="گزارش سرمایه‌گذار"
        type="custom"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              انتخاب سرمایه‌گذار
            </label>
            <SearchableSelect
              options={investors.map((user) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name}`,
              }))}
              value={summaryData.user_id}
              onChange={handleFetchSummary}
              placeholder="انتخاب کنید"
            />
          </div>
          {summaryData.user_id && (
            <div className="bg-persian-50 dark:bg-persian-900/20 border border-persian-100 dark:border-persian-800 rounded-xl p-4 text-center">
              <p className="text-sm text-persian-600 dark:text-persian-400 mb-2">
                مجموع سرمایه‌گذاری تا این لحظه
              </p>
              <p className="text-2xl font-bold text-persian-700 dark:text-persian-300">
                {formatCurrency(summaryData.total)}
              </p>
            </div>
          )}
          <button
            onClick={() => setIsSummaryModalOpen(false)}
            className="w-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 py-2.5 rounded-xl text-sm font-bold transition-colors"
          >
            بستن
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        title=""
        type="custom"
      >
        <div className="text-center py-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              modalState.type === "success"
                ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {modalState.type === "success" ? (
              <Check className="w-8 h-8" />
            ) : (
              <AlertCircle className="w-8 h-8" />
            )}
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            {modalState.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {modalState.message}
          </p>
          <button
            onClick={() =>
              setModalState((prev) => ({ ...prev, isOpen: false }))
            }
            className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            متوجه شدم
          </button>
        </div>
      </Modal>
    </div>
  );
}
